import { useEffect, useMemo } from "react";
import type { ModuleProps } from "@typedefs/modulesInterfaces";
import { useAppSelector, usePermissions } from "@app/hooks";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { selectCurrentProject } from "@features/project/projectSlice";
import { useFormikContext } from "formik";
import { EnergyEntryResponse, type ModuleFull } from "./energyTypes";
import { FEModules } from "@utils/moduleList";
import { isEqual } from 'lodash';
import { mergeLocalAndServerChanges } from "../inputs/utils";
import { useVCEnergyHandler } from "@utils/useVCEnergyHandler";
import {
	useCreateEnergyEntryMutation,
	useDeleteEnergyEntryMutation,
	useContextAwareGetEnergyEntries,
} from "@hooks/index";
import GenericModule from "../GenericModule";
import GenericOptionalSubModule from "../GenericOptionalSubmodule";

interface EnergyTierOneProps {
	initValues: ModuleFull;
	isEntriesLoading: boolean;
	setInitValues: React.Dispatch<React.SetStateAction<ModuleFull>>;
}

const EnergyTierOne = ({
	initValues,
	isEntriesLoading,
	setInitValues,
	...moduleProps
}: ModuleProps & EnergyTierOneProps) => {
	const { values } = useFormikContext<ModuleFull>()
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { isReadOnly } = usePermissions();
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	useVCEnergyHandler({ listInputName: "energyEntries", energyTypeInputName: "fuel_type" })

	const parentId = useMemo(() => moduleList.find((m) => m.id === FEModules.Energy)?.uniqueId, [moduleList]);
	const [deleteEnergyEntry, { isLoading: isLoadingDeleteEnergyEntry }] = useDeleteEnergyEntryMutation()
	const [createEnergyEntry, { isLoading: isLoadingCreateEnergyEntry }] = useCreateEnergyEntryMutation()

	const { data: entriesData } = useContextAwareGetEnergyEntries({ activityId: activeActivityId ?? 0 });
	const list = useMemo(() => {
		const isEntriesUpdated = !isEqual(values.energyEntries, entriesData)
		return isEntriesUpdated ? mergeLocalAndServerChanges({
			currentChangesList: values.energyEntries,
			incomingList: entriesData ?? [],
		}) : values.energyEntries
	}, [entriesData])

	useEffect(() => {
		if (list)
			setInitValues((cur) => ({ ...cur, energyEntries: list }))
	}, [list])

	const handleDeleteEnergyEntry = (
		remove: <T>(index: number) => T | undefined,
		index: number,
		id?: number | null,) => {
		remove(index)
		if (id) deleteEnergyEntry({ id, activityId: activeActivityId ?? 0, parent: parentId })
	}

	const handleCreateEnergyEntry = () => {
		createEnergyEntry({ activityId: activeActivityId ?? 0, parent: parentId })
	}


	return (
		<GenericModule {...moduleProps} disableOptionals>
			{
				() => (
					<>
						{moduleProps.moduleSchema.optionalSubmodules?.map((subModule, index) => {
							return (
								<GenericOptionalSubModule<EnergyEntryResponse>
									key={`${subModule.name}-${index}`}
									optionalSchema={subModule}
									values={values?.energyEntries}
									addLabel="module.add_new_energy"
									createHandler={handleCreateEnergyEntry}
									removeHandler={handleDeleteEnergyEntry}
									fieldArrayName={subModule.name}
									isOptionalLoading={isEntriesLoading || isLoadingCreateEnergyEntry || isLoadingDeleteEnergyEntry}
									isCreateLoading={isLoadingCreateEnergyEntry}
									isDeleteLoading={isLoadingDeleteEnergyEntry}
									isReadOnly={isReadOnly}
								/>
							)
						})}
					</>
				)
			}
		</GenericModule>
	)
};

export default EnergyTierOne;